import React, { useEffect, useState } from "react";
import { adminApi, API } from "@/lib/lnlApi";
import { toast } from "sonner";
import { Save, Upload, Trash2, Archive, Undo2, Download, FileText, Image as ImageIcon, Plus, Eye, EyeOff } from "lucide-react";
import SocialSettings from "./SocialSettings";

function formatErr(e) {
    const d = e?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
    return d?.msg || e?.message || "Something went wrong.";
}

// ============================================================================
// Settings / Homepage / SEO / Social
// ============================================================================
export function SettingsPanel() {
    const [s, setS] = useState(null);
    const [saving, setSaving] = useState(false);
    const [pickerFor, setPickerFor] = useState(null); // { field, label }
    const [history, setHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    async function load() { const { data } = await adminApi.get("/cms/settings"); setS(data); }
    async function loadHistory() {
        try { const { data } = await adminApi.get("/cms/settings/history", { params: { limit: 20 } }); setHistory(data); }
        catch (e) { /* ignore */ }
    }
    useEffect(() => { load(); loadHistory(); }, []);
    async function save() {
        setSaving(true);
        try { await adminApi.patch("/cms/settings", s); toast.success("Settings saved"); loadHistory(); }
        catch (e) { toast.error(formatErr(e)); }
        finally { setSaving(false); }
    }
    async function restoreSnap(snap_id) {
        if (!window.confirm("Restore website settings to this snapshot? Your current values will be snapshotted first so you can undo the restore.")) return;
        try { await adminApi.post(`/cms/settings/restore/${snap_id}`); toast.success("Settings restored"); await load(); await loadHistory(); }
        catch (e) { toast.error(formatErr(e)); }
    }
    if (!s) return <div className="text-lnl-gray">Loading settings…</div>;
    const F = ({ k, label, hint, type = "text", wide = false }) => (
        <div className={wide ? "sm:col-span-2" : ""}>
            <label className="text-xs text-lnl-gray font-heading font-semibold">{label}</label>
            <input type={type} value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2" data-testid={`settings-${k}`} />
            {hint && <div className="text-[11px] text-lnl-gray mt-1">{hint}</div>}
        </div>
    );
    const ImageField = ({ k, label }) => (
        <div className="sm:col-span-2">
            <label className="text-xs text-lnl-gray font-heading font-semibold">{label}</label>
            <div className="mt-1 flex gap-3 items-start">
                <div className="w-32 h-20 rounded-md border border-lnl-border bg-lnl-off-white flex items-center justify-center overflow-hidden shrink-0">
                    {s[k]
                        ? <img src={s[k]} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                        : <ImageIcon className="h-6 w-6 text-lnl-gray" />}
                </div>
                <div className="flex-1 min-w-0">
                    <input type="text" value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} className="w-full rounded-md border border-lnl-border px-3 py-2 text-sm" placeholder="Paste an image URL or pick from library" data-testid={`settings-${k}`} />
                    <div className="mt-1.5 flex gap-2">
                        <button onClick={() => setPickerFor({ field: k, label })} className="lnl-btn-secondary text-xs py-1.5" data-testid={`pick-${k}`}><ImageIcon className="h-3.5 w-3.5" /> Choose from library</button>
                        {s[k] && <button onClick={() => setS({ ...s, [k]: "" })} className="text-xs text-rose-700 hover:underline font-heading font-semibold">Clear</button>}
                    </div>
                </div>
            </div>
        </div>
    );
    const Group = ({ title, children }) => (
        <div className="lnl-card p-5">
            <div className="lnl-eyebrow">{title}</div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
        </div>
    );
    return (
        <div className="space-y-5" data-testid="settings-panel">
            <Group title="Institution">
                <F k="official_name" label="Official name" wide />
                <F k="abbreviation" label="Abbreviation" />
                <F k="academic_year" label="Academic year" />
                <F k="enrollment_status" label="Enrollment status" />
                <F k="office_hours_draft" label="Office hours" wide />
            </Group>
            <Group title="Contact">
                <F k="mobile" label="Mobile" />
                <F k="email_info" label="General information email (shown first)" />
                <F k="email_registrar" label="Registrar email (shown second)" />
                <F k="email_admissions" label="Admissions email (internal — not shown publicly)" />
                <F k="email_library" label="Library email" />
                <F k="address_line" label="Street address" />
                <F k="province_line" label="City / province" />
                <F k="map_query" label="Map query" wide />
            </Group>
            <Group title="External systems">
                <F k="portal_url" label="Student Portal URL" />
                <F k="library_url" label="Library URL" />
                <F k="opac_url" label="OPAC URL" />
                <F k="primary_domain" label="Primary domain" />
                <F k="android_package" label="Android package" />
            </Group>
            <Group title="Homepage hero">
                <F k="hero_school_name" label="School name shown in hero" wide hint="Leave blank to use the Official name. Slide headlines, buttons and images are edited under Hero Slides." />
                <F k="hero_headline" label="Hero headline" wide hint="Leave blank to use default." />
                <F k="hero_subtitle" label="Hero subtitle" wide />
                <F k="hero_badge" label="Hero badge (e.g. Admissions Now Open)" />
                <ImageField k="hero_image_url" label="Hero image" />
            </Group>
            <SocialSettings s={s} setS={setS} />
            <Group title="Contact Us button (header nav)">
                <div>
                    <label className="text-xs text-lnl-gray font-heading font-semibold">Show CONTACT button in header</label>
                    <select value={String(s.contact_button_enabled ?? true)} onChange={(e) => setS({ ...s, contact_button_enabled: e.target.value === "true" })} className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2 bg-white" data-testid="settings-contact_button_enabled">
                        <option value="true">Show</option>
                        <option value="false">Hide</option>
                    </select>
                </div>
                <div>
                    <label className="text-xs text-lnl-gray font-heading font-semibold">Button style</label>
                    <select value={s.contact_button_style || "primary"} onChange={(e) => setS({ ...s, contact_button_style: e.target.value })} className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2 bg-white" data-testid="settings-contact_button_style">
                        <option value="primary">Primary (green)</option>
                        <option value="secondary">Secondary</option>
                        <option value="outline">Outline</option>
                    </select>
                </div>
                <F k="contact_button_text" label="Button label" hint='Default: "Contact Us"' />
                <F k="contact_button_url" label="Button link URL" hint="Internal path (/contact) or full URL / mailto: / tel:" />
            </Group>
            <Group title="Email notifications">
                <div className="sm:col-span-2">
                    <label className="text-xs text-lnl-gray font-heading font-semibold">Master switch</label>
                    <select value={String(s.email_notifications_enabled ?? true)} onChange={(e) => setS({ ...s, email_notifications_enabled: e.target.value === "true" })} className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2 bg-white" data-testid="settings-email_notifications_enabled">
                        <option value="true">Enabled — send department notification emails</option>
                        <option value="false">Disabled — do not send any automated emails</option>
                    </select>
                    <div className="text-[11px] text-lnl-gray mt-1">Individual staff notification preferences (per user) are managed in Users &amp; Roles.</div>
                </div>
            </Group>
            <Group title="SEO & analytics">
                <F k="seo_title" label="Default SEO title" wide />
                <F k="seo_description" label="Default meta description" wide />
                <ImageField k="seo_og_image" label="Open Graph image" />
                <F k="ga4_id" label="Google Analytics 4 ID" />
            </Group>

            <div className="flex flex-wrap justify-between items-center gap-3">
                <button onClick={() => setShowHistory((v) => !v)} className="lnl-btn-secondary text-sm" data-testid="settings-history-toggle">
                    <Undo2 className="h-4 w-4" /> {showHistory ? "Hide" : "Show"} history ({history.length})
                </button>
                <button onClick={save} disabled={saving} className="lnl-btn-primary" data-testid="settings-save"><Save className="h-4 w-4" /> {saving ? "Saving…" : "Save all settings"}</button>
            </div>

            {showHistory && (
                <div className="lnl-card p-4" data-testid="settings-history">
                    <div className="lnl-eyebrow mb-2">Recent settings changes</div>
                    {history.length === 0 && <div className="text-sm text-lnl-gray">No history yet.</div>}
                    <ul className="divide-y divide-lnl-border">
                        {history.map((h) => (
                            <li key={h.id} className="py-2 flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="text-xs text-lnl-gray">{new Date(h.at).toLocaleString()} · <b>{h.actor_email}</b></div>
                                    <div className="text-sm font-heading font-semibold text-lnl-charcoal">
                                        {h.kind === "pre_restore" ? "Pre-restore snapshot" : `Changed: ${(h.fields || []).join(", ")}`}
                                    </div>
                                </div>
                                <button onClick={() => restoreSnap(h.id)} className="text-xs text-lnl-green-deep font-heading font-semibold hover:underline whitespace-nowrap" data-testid={`restore-${h.id}`}>Restore</button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <MediaPicker
                open={!!pickerFor}
                onClose={() => setPickerFor(null)}
                onPick={(m) => setS({ ...s, [pickerFor.field]: `${API}/cms/media/${m.id}/download` })}
            />
        </div>
    );
}

// ============================================================================
// Media Library
// ============================================================================
export function MediaPanel({ pickMode, onPick, kindFilter }) {
    const [items, setItems] = useState([]);
    const [status, setStatus] = useState("active");
    const [kind, setKind] = useState(kindFilter || "");
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState(null); // { total, done, current }
    const [dragOver, setDragOver] = useState(false);
    const [q, setQ] = useState("");

    async function load() {
        const { data } = await adminApi.get("/cms/media", { params: { status, kind: kind || undefined } });
        setItems(data);
    }
    useEffect(() => { load(); }, [status, kind]);

    async function uploadFiles(files) {
        if (!files || files.length === 0) return;
        setBusy(true);
        setProgress({ total: files.length, done: 0, current: files[0]?.name });
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            setProgress({ total: files.length, done: i, current: file.name });
            const form = new FormData();
            form.append("file", file);
            form.append("title", file.name);
            try { await adminApi.post("/cms/media", form, { headers: { "Content-Type": "multipart/form-data" } }); }
            catch (err) { toast.error(`${file.name}: ${formatErr(err)}`); }
        }
        setProgress(null); setBusy(false); toast.success(`Uploaded ${files.length} file${files.length === 1 ? "" : "s"}`); load();
    }

    async function onFilePick(e) {
        const files = Array.from(e.target.files || []);
        await uploadFiles(files);
        e.target.value = "";
    }

    function onDrop(e) {
        e.preventDefault(); setDragOver(false);
        const files = Array.from(e.dataTransfer.files || []).filter((f) => /^(image|application\/pdf)/.test(f.type));
        uploadFiles(files);
    }

    async function archive(id) { try { await adminApi.post(`/cms/media/${id}/archive`); toast.success("Archived"); load(); } catch (e) { toast.error(formatErr(e)); } }
    async function restore(id) { try { await adminApi.post(`/cms/media/${id}/restore`); toast.success("Restored"); load(); } catch (e) { toast.error(formatErr(e)); } }
    async function del(id) { if (!window.confirm("Delete permanently?")) return; try { await adminApi.delete(`/cms/media/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatErr(e)); } }

    const shown = q ? items.filter((m) => (m.title || "").toLowerCase().includes(q.toLowerCase()) || (m.original_filename || "").toLowerCase().includes(q.toLowerCase())) : items;

    return (
        <div data-testid="media-panel">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                    {["active", "archived"].map((v) => (
                        <button key={v} onClick={() => setStatus(v)} className={`text-xs font-heading font-semibold rounded-full px-3 py-1.5 border ${status === v ? "bg-lnl-green text-white border-lnl-green" : "bg-white border-lnl-border"}`}>{v}</button>
                    ))}
                    {!kindFilter && (
                        <select value={kind} onChange={(e) => setKind(e.target.value)} className="text-xs rounded-md border border-lnl-border px-2 py-1.5 bg-white">
                            <option value="">All types</option>
                            <option value="image">Images</option>
                            <option value="document">Documents</option>
                        </select>
                    )}
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search filename…" className="text-xs rounded-md border border-lnl-border px-3 py-1.5 w-56" data-testid="media-search" />
                </div>
            </div>

            {/* Drag-and-drop upload zone */}
            <div
                onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                className={`mt-4 border-2 border-dashed rounded-lg p-6 text-center transition-colors ${dragOver ? "border-lnl-green bg-lnl-green-bg" : "border-lnl-border bg-lnl-off-white"}`}
                data-testid="media-dropzone"
            >
                <Upload className="h-6 w-6 mx-auto text-lnl-green-deep" />
                <div className="mt-2 font-heading font-semibold text-lnl-charcoal">
                    {dragOver ? "Release to upload" : "Drag & drop images or PDFs here"}
                </div>
                <div className="text-xs text-lnl-gray mt-1">or</div>
                <label className="mt-2 inline-flex lnl-btn-primary cursor-pointer" data-testid="media-upload">
                    <Upload className="h-4 w-4" /> {busy ? "Uploading…" : "Select files"}
                    <input type="file" multiple className="hidden" onChange={onFilePick} accept="image/png,image/jpeg,image/webp,image/gif,application/pdf,text/csv" />
                </label>
                {progress && (
                    <div className="mt-3 text-xs text-lnl-gray">
                        Uploading {progress.done + 1}/{progress.total} — <span className="text-lnl-charcoal">{progress.current}</span>
                        <div className="mt-1 h-1.5 bg-lnl-border rounded-full overflow-hidden max-w-md mx-auto">
                            <div className="h-full bg-lnl-green transition-all" style={{ width: `${((progress.done) / progress.total) * 100}%` }} />
                        </div>
                    </div>
                )}
                <div className="text-[11px] text-lnl-gray mt-2">Max 15MB per file · JPG / PNG / WEBP / GIF / PDF</div>
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {shown.map((m) => {
                    const thumb = m.kind === "image" ? `${API}/cms/media/${m.id}/download` : null;
                    return (
                        <div key={m.id} className="lnl-card p-3">
                            <div className="aspect-video bg-lnl-off-white rounded-md flex items-center justify-center overflow-hidden">
                                {thumb
                                    ? <img src={thumb} alt={m.alt_text || m.title} className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                    : <FileText className="h-8 w-8 text-lnl-gray" />}
                            </div>
                            <div className="mt-2 text-xs">
                                <div className="font-heading font-semibold text-lnl-charcoal truncate" title={m.title}>{m.title || m.original_filename}</div>
                                <div className="text-lnl-gray truncate">{m.original_filename} · {(m.size / 1024).toFixed(1)} KB</div>
                            </div>
                            <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
                                {pickMode && <button onClick={() => onPick?.(m)} className="text-lnl-green-deep font-heading font-semibold hover:underline" data-testid={`media-pick-${m.id}`}>Use</button>}
                                <a href={`${API}/cms/media/${m.id}/download`} onClick={(e) => { e.preventDefault(); adminApi.get(`/cms/media/${m.id}/download`, { responseType: "blob" }).then((r) => { const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = m.original_filename || m.id; a.click(); URL.revokeObjectURL(url); }); }} className="text-lnl-charcoal hover:underline inline-flex items-center gap-1"><Download className="h-3 w-3" /> Download</a>
                                {m.is_archived
                                    ? <button onClick={() => restore(m.id)} className="text-emerald-700 hover:underline inline-flex items-center gap-1"><Undo2 className="h-3 w-3" /> Restore</button>
                                    : <button onClick={() => archive(m.id)} className="text-amber-800 hover:underline inline-flex items-center gap-1"><Archive className="h-3 w-3" /> Archive</button>
                                }
                                <button onClick={() => del(m.id)} className="text-rose-700 hover:underline inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /> Delete</button>
                            </div>
                        </div>
                    );
                })}
                {shown.length === 0 && <div className="col-span-full p-10 text-center text-lnl-gray border border-dashed border-lnl-border rounded-md">No media yet.</div>}
            </div>
        </div>
    );
}

// Reusable Media Picker — opens the Media Library in pick mode for image fields.
export function MediaPicker({ open, onClose, onPick, kind = "image" }) {
    if (!open) return null;
    return (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" data-testid="media-picker">
            <div className="w-full max-w-4xl max-h-[85vh] overflow-y-auto bg-white rounded-lg border border-lnl-border shadow-xl">
                <div className="sticky top-0 bg-white flex items-center justify-between px-4 py-3 border-b border-lnl-border">
                    <div className="font-heading font-bold text-lnl-charcoal">Select an image from the Media Library</div>
                    <button onClick={onClose} className="text-lnl-gray hover:text-lnl-charcoal" aria-label="Close">✕</button>
                </div>
                <div className="p-4">
                    <MediaPanel pickMode kindFilter={kind} onPick={(m) => { onPick(m); onClose(); }} />
                </div>
            </div>
        </div>
    );
}

// ============================================================================
// Generic list + create/edit for News, FAQs, Facilities, Leadership, Programs
// ============================================================================
function StatusPill({ status }) {
    const map = { published: "bg-emerald-50 text-emerald-800 border-emerald-200", draft: "bg-neutral-100 text-neutral-700 border-neutral-200", archived: "bg-amber-50 text-amber-800 border-amber-200", approved: "bg-emerald-50 text-emerald-800 border-emerald-200", for_review: "bg-amber-50 text-amber-800 border-amber-200" };
    return <span className={`text-[10px] px-2 py-0.5 rounded border font-heading font-semibold uppercase tracking-wider ${map[status] || "bg-neutral-100 text-neutral-700 border-neutral-200"}`}>{status}</span>;
}

export function NewsPanel() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);
    async function load() { const { data } = await adminApi.get("/cms/news"); setItems(data); }
    useEffect(() => { load(); }, []);
    async function state(slug, s) { try { await adminApi.post(`/cms/news/${slug}/${s}`); toast.success(`${s}ed`); load(); } catch (e) { toast.error(formatErr(e)); } }
    async function del(slug) { if (!window.confirm("Delete this item?")) return; try { await adminApi.delete(`/cms/news/${slug}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatErr(e)); } }

    return (
        <div data-testid="news-panel">
            <div className="flex items-center justify-between mb-3">
                <div className="font-heading font-bold text-lnl-charcoal">News, announcements & events</div>
                <button onClick={() => setCreating(true)} className="lnl-btn-primary"><Plus className="h-4 w-4" /> New item</button>
            </div>
            <table className="w-full text-sm border-collapse">
                <thead className="text-left text-xs uppercase tracking-widest text-lnl-gray"><tr><th className="p-2">Title</th><th className="p-2">Category</th><th className="p-2">Status</th><th className="p-2">Actions</th></tr></thead>
                <tbody>
                    {items.map((n) => (
                        <tr key={n.slug} className="border-t border-lnl-border">
                            <td className="p-2">
                                <div className="font-heading font-semibold text-lnl-charcoal">{n.title}</div>
                                <div className="text-xs text-lnl-gray">{n.slug}</div>
                            </td>
                            <td className="p-2">{n.category}</td>
                            <td className="p-2"><StatusPill status={n.status} /></td>
                            <td className="p-2 space-x-2">
                                <button onClick={() => setEditing(n)} className="text-xs font-heading font-semibold text-lnl-green-deep hover:underline">Edit</button>
                                {n.status !== "published" && <button onClick={() => state(n.slug, "publish")} className="text-xs font-heading font-semibold text-emerald-700 hover:underline inline-flex items-center gap-1"><Eye className="h-3 w-3" /> Publish</button>}
                                {n.status === "published" && <button onClick={() => state(n.slug, "unpublish")} className="text-xs font-heading font-semibold text-amber-800 hover:underline inline-flex items-center gap-1"><EyeOff className="h-3 w-3" /> Unpublish</button>}
                                {n.status !== "archived" ? <button onClick={() => state(n.slug, "archive")} className="text-xs font-heading font-semibold text-amber-800 hover:underline">Archive</button> : <button onClick={() => state(n.slug, "restore")} className="text-xs font-heading font-semibold text-emerald-700 hover:underline">Restore</button>}
                                <button onClick={() => del(n.slug)} className="text-xs font-heading font-semibold text-rose-700 hover:underline">Delete</button>
                            </td>
                        </tr>
                    ))}
                    {items.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-lnl-gray">No items yet.</td></tr>}
                </tbody>
            </table>
            {(editing || creating) && <NewsEditor item={editing} onClose={() => { setEditing(null); setCreating(false); }} onSaved={() => { setEditing(null); setCreating(false); load(); }} />}
        </div>
    );
}

function NewsEditor({ item, onClose, onSaved }) {
    const [f, setF] = useState(item || { slug: "", title: "", category: "Announcements", summary: "", body: "", featured: false, status: "draft", event_start: "", event_end: "", venue: "" });
    const [busy, setBusy] = useState(false);
    async function submit(e) {
        e.preventDefault(); setBusy(true);
        try {
            if (item) await adminApi.patch(`/cms/news/${item.slug}`, f);
            else await adminApi.post("/cms/news", f);
            toast.success("Saved"); onSaved();
        } catch (err) { toast.error(formatErr(err)); }
        finally { setBusy(false); }
    }
    return (
        <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4">
            <div className="w-full max-w-2xl bg-white rounded-md border border-lnl-border shadow-xl max-h-[85vh] overflow-y-auto">
                <div className="px-4 py-3 border-b border-lnl-border font-heading font-bold text-lnl-charcoal">{item ? "Edit item" : "New item"}</div>
                <form onSubmit={submit} className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[["slug","Slug"],["title","Title"],["category","Category"],["summary","Summary"],["venue","Venue"],["event_start","Event start"],["event_end","Event end"]].map(([k, l]) => (
                        <div key={k} className={k === "title" || k === "summary" ? "sm:col-span-2" : ""}>
                            <label className="text-xs text-lnl-gray font-heading font-semibold">{l}</label>
                            <input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2" disabled={item && k === "slug"} required={k === "slug" || k === "title"} />
                        </div>
                    ))}
                    <div className="sm:col-span-2">
                        <label className="text-xs text-lnl-gray font-heading font-semibold">Body</label>
                        <textarea value={f.body || ""} onChange={(e) => setF({ ...f, body: e.target.value })} rows={6} className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2" />
                    </div>
                    <div>
                        <label className="text-xs text-lnl-gray font-heading font-semibold">Status</label>
                        <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2 bg-white">
                            <option value="draft">Draft</option><option value="published">Published</option><option value="archived">Archived</option>
                        </select>
                    </div>
                    <label className="flex items-end gap-2"><input type="checkbox" checked={!!f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} /> <span className="text-sm">Featured</span></label>
                    <div className="sm:col-span-2 flex justify-end gap-2 mt-2">
                        <button type="button" onClick={onClose} className="lnl-btn-secondary">Cancel</button>
                        <button disabled={busy} className="lnl-btn-primary">{busy ? "Saving…" : "Save"}</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Simple CRUD panel factory for FAQs, Facilities, Leadership
// ---------------------------------------------------------------------------
function CrudPanel({ endpoint, testid, columns, fields, sortKey, primaryKey = "id" }) {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);
    async function load() { const { data } = await adminApi.get(endpoint); setItems(sortKey ? [...data].sort((a, b) => (a[sortKey] || 0) - (b[sortKey] || 0)) : data); }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
    async function del(id) { if (!window.confirm("Delete?")) return; try { await adminApi.delete(`${endpoint}/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatErr(e)); } }
    async function save(payload, id) {
        try {
            if (id) await adminApi.patch(`${endpoint}/${id}`, payload);
            else await adminApi.post(endpoint, payload);
            toast.success("Saved"); load(); setEditing(null); setCreating(false);
        } catch (err) { toast.error(formatErr(err)); }
    }
    return (
        <div data-testid={testid}>
            <div className="flex items-center justify-between mb-3">
                <div className="font-heading font-bold text-lnl-charcoal">{testid.replace("-panel", "").replace(/-/g, " ")}</div>
                <button onClick={() => setCreating(true)} className="lnl-btn-primary"><Plus className="h-4 w-4" /> New</button>
            </div>
            <table className="w-full text-sm border-collapse">
                <thead className="text-left text-xs uppercase tracking-widest text-lnl-gray"><tr>{columns.map((c) => <th key={c.key} className="p-2">{c.label}</th>)}<th className="p-2">Actions</th></tr></thead>
                <tbody>
                    {items.map((it) => (
                        <tr key={it[primaryKey]} className="border-t border-lnl-border">
                            {columns.map((c) => <td key={c.key} className="p-2">{c.render ? c.render(it) : (it[c.key] ?? "—")}</td>)}
                            <td className="p-2 space-x-2">
                                <button onClick={() => setEditing(it)} className="text-xs font-heading font-semibold text-lnl-green-deep hover:underline">Edit</button>
                                <button onClick={() => del(it[primaryKey])} className="text-xs font-heading font-semibold text-rose-700 hover:underline">Delete</button>
                            </td>
                        </tr>
                    ))}
                    {items.length === 0 && <tr><td colSpan={columns.length + 1} className="p-6 text-center text-lnl-gray">No items.</td></tr>}
                </tbody>
            </table>
            {(editing || creating) && <CrudEditor initial={editing} fields={fields} onClose={() => { setEditing(null); setCreating(false); }} onSave={(payload) => save(payload, editing?.[primaryKey])} />}
        </div>
    );
}

function CrudEditor({ initial, fields, onClose, onSave }) {
    const [f, setF] = useState(initial || Object.fromEntries(fields.map((x) => [x.key, x.default ?? ""])));
    return (
        <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4">
            <div className="w-full max-w-lg bg-white rounded-md border border-lnl-border shadow-xl max-h-[85vh] overflow-y-auto">
                <div className="px-4 py-3 border-b border-lnl-border font-heading font-bold text-lnl-charcoal">{initial ? "Edit" : "Create"}</div>
                <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {fields.map((x) => (
                        <div key={x.key} className={x.wide ? "sm:col-span-2" : ""}>
                            <label className="text-xs text-lnl-gray font-heading font-semibold">{x.label}</label>
                            {x.type === "textarea" ? (
                                <textarea value={f[x.key] || ""} onChange={(e) => setF({ ...f, [x.key]: e.target.value })} rows={4} className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2" />
                            ) : x.type === "select" ? (
                                <select value={f[x.key] || ""} onChange={(e) => setF({ ...f, [x.key]: e.target.value })} className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2 bg-white">
                                    {x.options.map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
                                </select>
                            ) : x.type === "number" ? (
                                <input type="number" value={f[x.key] ?? 0} onChange={(e) => setF({ ...f, [x.key]: Number(e.target.value) })} className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2" />
                            ) : (
                                <input value={f[x.key] || ""} onChange={(e) => setF({ ...f, [x.key]: e.target.value })} className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2" required={!!x.required} />
                            )}
                        </div>
                    ))}
                    <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="lnl-btn-secondary">Cancel</button>
                        <button className="lnl-btn-primary">Save</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export const FaqsPanel = () => (
    <CrudPanel endpoint="/cms/faqs" testid="faqs-panel"
        columns={[{ key: "category", label: "Category" }, { key: "question", label: "Question" }]}
        fields={[
            { key: "category", label: "Category", required: true },
            { key: "question", label: "Question", wide: true, required: true },
            { key: "answer", label: "Answer", type: "textarea", wide: true, required: true },
        ]} />
);

export const FacilitiesPanel = () => (
    <CrudPanel endpoint="/cms/facilities" testid="facilities-panel" sortKey="order"
        columns={[{ key: "name", label: "Name" }, { key: "category", label: "Category" }, { key: "status", label: "Status", render: (it) => <StatusPill status={it.status} /> }]}
        fields={[
            { key: "name", label: "Name", required: true },
            { key: "category", label: "Category", type: "select", options: ["Classroom", "Laboratory", "Library", "Sports", "Guidance", "Facility", "Other"], default: "Classroom" },
            { key: "description", label: "Description", type: "textarea", wide: true },
            { key: "order", label: "Display order", type: "number", default: 100 },
            { key: "status", label: "Status", type: "select", options: ["draft", "published", "archived"], default: "draft" },
        ]} />
);

export const LeadershipPanel = () => (
    <CrudPanel endpoint="/cms/leadership" testid="leadership-panel" sortKey="order"
        columns={[{ key: "name", label: "Name" }, { key: "title", label: "Title" }, { key: "publication_status", label: "Status", render: (it) => <StatusPill status={it.publication_status} /> }]}
        fields={[
            { key: "name", label: "Full name", required: true },
            { key: "title", label: "Position title", required: true },
            { key: "department", label: "Department" },
            { key: "bio", label: "Short bio", type: "textarea", wide: true },
            { key: "order", label: "Display order", type: "number", default: 100 },
            { key: "publication_status", label: "Publication status", type: "select", options: ["draft", "for_review", "approved"], default: "for_review" },
        ]} />
);

// ============================================================================
// Statements editor (Vision/Mission/Philosophy/Outcomes/History/Hymn)
// ============================================================================
export function StatementsPanel() {
    const [items, setItems] = useState([]);
    async function load() { const { data } = await adminApi.get("/statements"); setItems(data); }
    useEffect(() => { load(); }, []);
    async function save(s) {
        try { await adminApi.patch(`/cms/statements/${s.code}`, { title: s.title, body: s.body, status: s.status, notice: s.notice }); toast.success("Saved"); load(); }
        catch (e) { toast.error(formatErr(e)); }
    }
    return (
        <div className="space-y-3" data-testid="statements-panel">
            {items.map((s) => (
                <div key={s.code} className="lnl-card p-4">
                    <div className="flex items-center justify-between">
                        <div className="font-heading font-bold text-lnl-charcoal">{s.title} <span className="ml-2"><StatusPill status={s.status} /></span></div>
                        <select value={s.status} onChange={(e) => setItems(items.map((x) => x.code === s.code ? { ...x, status: e.target.value } : x))} className="text-xs border border-lnl-border rounded px-2 py-1 bg-white">
                            <option value="draft">Draft</option><option value="for_review">For review</option><option value="approved">Approved</option>
                        </select>
                    </div>
                    <input value={s.title || ""} onChange={(e) => setItems(items.map((x) => x.code === s.code ? { ...x, title: e.target.value } : x))} className="mt-2 w-full rounded-md border border-lnl-border px-3 py-2" />
                    <textarea value={s.body || ""} onChange={(e) => setItems(items.map((x) => x.code === s.code ? { ...x, body: e.target.value } : x))} rows={s.code === "hymn" ? 12 : 5} className="mt-2 w-full rounded-md border border-lnl-border px-3 py-2 font-mono text-sm" />
                    <div className="mt-2 flex justify-end"><button onClick={() => save(s)} className="lnl-btn-primary"><Save className="h-4 w-4" /> Save {s.title}</button></div>
                </div>
            ))}
        </div>
    );
}

// ============================================================================
// Programs editor
// ============================================================================
export function ProgramsPanel() {
    const [items, setItems] = useState([]);
    const [depts, setDepts] = useState([]);
    const [editing, setEditing] = useState(null);
    async function load() {
        const [p, d] = await Promise.all([adminApi.get("/cms/programs"), adminApi.get("/departments")]);
        setItems(p.data); setDepts(d.data);
    }
    useEffect(() => { load(); }, []);
    async function save(p) {
        try { await adminApi.patch(`/cms/programs/${p.slug}`, p); toast.success("Saved"); load(); setEditing(null); }
        catch (e) { toast.error(formatErr(e)); }
    }
    return (
        <div data-testid="programs-panel">
            <div className="font-heading font-bold text-lnl-charcoal mb-3">Academic programs</div>
            <table className="w-full text-sm border-collapse">
                <thead className="text-left text-xs uppercase tracking-widest text-lnl-gray"><tr><th className="p-2">Program</th><th className="p-2">Department</th><th className="p-2">Status</th><th className="p-2">Actions</th></tr></thead>
                <tbody>
                    {items.map((p) => (
                        <tr key={p.slug} className="border-t border-lnl-border">
                            <td className="p-2"><div className="font-heading font-semibold">{p.name}</div><div className="text-xs text-lnl-gray">{p.slug}</div></td>
                            <td className="p-2">{depts.find((d) => d.slug === p.department_slug)?.name || p.department_slug}</td>
                            <td className="p-2"><StatusPill status={p.status || "published"} /></td>
                            <td className="p-2"><button onClick={() => setEditing(p)} className="text-xs font-heading font-semibold text-lnl-green-deep hover:underline">Edit</button></td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {editing && (
                <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4">
                    <div className="w-full max-w-2xl bg-white rounded-md border border-lnl-border shadow-xl">
                        <div className="px-4 py-3 border-b border-lnl-border font-heading font-bold text-lnl-charcoal">Edit {editing.name}</div>
                        <div className="p-4 space-y-3">
                            <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="w-full rounded-md border border-lnl-border px-3 py-2" />
                            <textarea value={editing.overview || ""} onChange={(e) => setEditing({ ...editing, overview: e.target.value })} rows={4} className="w-full rounded-md border border-lnl-border px-3 py-2" />
                            <select value={editing.status || "published"} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="rounded-md border border-lnl-border px-3 py-2 bg-white">
                                <option value="published">Published</option><option value="draft">Draft</option><option value="archived">Archived</option>
                            </select>
                            <div className="flex justify-end gap-2">
                                <button onClick={() => setEditing(null)} className="lnl-btn-secondary">Cancel</button>
                                <button onClick={() => save(editing)} className="lnl-btn-primary">Save</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ============================================================================
// Requirements editor
// ============================================================================
export function RequirementsPanel() {
    const [items, setItems] = useState([]);
    const [selected, setSelected] = useState(null);
    async function load() { const { data } = await adminApi.get("/requirements"); setItems(data); }
    useEffect(() => { load(); }, []);
    async function save() {
        try { await adminApi.patch(`/cms/requirements/${selected.applicant_type}`, { title: selected.title, items: selected.items, notice: selected.notice }); toast.success("Saved"); load(); }
        catch (e) { toast.error(formatErr(e)); }
    }
    return (
        <div data-testid="requirements-panel" className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            <aside className="lg:col-span-1">
                <div className="lnl-eyebrow mb-2">Applicant type</div>
                <div className="flex flex-col gap-1">
                    {items.map((it) => (
                        <button key={it.applicant_type} onClick={() => setSelected({ ...it })} className={`text-left text-sm px-3 py-2 rounded-md font-heading font-semibold ${selected?.applicant_type === it.applicant_type ? "bg-lnl-green-bg text-lnl-green-deep" : "hover:bg-lnl-off-white"}`}>{it.title}</button>
                    ))}
                </div>
            </aside>
            <div className="lg:col-span-3">
                {!selected ? <div className="text-lnl-gray">Select an applicant type to edit.</div> : (
                    <div className="lnl-card p-4">
                        <label className="text-xs text-lnl-gray font-heading font-semibold">Section title</label>
                        <input value={selected.title} onChange={(e) => setSelected({ ...selected, title: e.target.value })} className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2" />
                        <label className="text-xs text-lnl-gray font-heading font-semibold mt-3 block">Requirement items (one per line)</label>
                        <textarea value={(selected.items || []).join("\n")} onChange={(e) => setSelected({ ...selected, items: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })} rows={10} className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2 font-mono text-sm" />
                        <label className="text-xs text-lnl-gray font-heading font-semibold mt-3 block">Notice (optional)</label>
                        <input value={selected.notice || ""} onChange={(e) => setSelected({ ...selected, notice: e.target.value })} className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2" />
                        <div className="mt-3 flex justify-end"><button onClick={save} className="lnl-btn-primary"><Save className="h-4 w-4" /> Save requirements</button></div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ============================================================================
// Export panel
// ============================================================================
export function ExportPanel() {
    const [rows, setRows] = useState([]);
    useEffect(() => { adminApi.get("/cms/export/index").then((r) => setRows(r.data)); }, []);
    async function download(key) {
        try {
            const r = await adminApi.get(`/cms/export/${key}.csv`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = `lnl_${key}.csv`; a.click();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error(formatErr(e)); }
    }
    return (
        <div data-testid="export-panel">
            <div className="font-heading font-bold text-lnl-charcoal mb-3">Export data</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {rows.map((r) => (
                    <div key={r.key} className="lnl-card p-4">
                        <div className="font-heading font-semibold capitalize">{r.key.replace(/_/g, " ")}</div>
                        <div className="text-xs text-lnl-gray mt-1">Columns: {r.columns.join(", ")}</div>
                        <button onClick={() => download(r.key)} className="mt-3 lnl-btn-secondary" data-testid={`export-${r.key}`}><Download className="h-4 w-4" /> Download CSV</button>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ============================================================================
// Hero slides — CMS
// ============================================================================
export function HeroSlidesPanel() {
    const [items, setItems] = useState([]);
    const [pickerFor, setPickerFor] = useState(null); // slide.id
    const [busy, setBusy] = useState(false);

    async function load() {
        const { data } = await adminApi.get("/cms/hero-slides");
        setItems(data);
    }
    useEffect(() => { load(); }, []);

    async function addSlide() {
        setBusy(true);
        try {
            await adminApi.post("/cms/hero-slides", {
                enabled: true, headline: "New slide", primary_cta_text: "Learn more",
                primary_cta_url: "/", duration_seconds: 6, overlay_opacity: 0.55,
                motion_style: "ken_burns",
            });
            toast.success("Slide added");
            await load();
        } catch (e) { toast.error(formatErr(e)); }
        finally { setBusy(false); }
    }

    async function patch(id, changes) {
        try {
            await adminApi.patch(`/cms/hero-slides/${id}`, changes);
            setItems((prev) => prev.map((s) => (s.id === id ? { ...s, ...changes } : s)));
        } catch (e) { toast.error(formatErr(e)); }
    }

    async function del(id) {
        if (!window.confirm("Delete this slide?")) return;
        try { await adminApi.delete(`/cms/hero-slides/${id}`); toast.success("Slide deleted"); load(); }
        catch (e) { toast.error(formatErr(e)); }
    }

    async function move(id, direction) {
        const ordered = [...items].sort((a, b) => a.order - b.order);
        const i = ordered.findIndex((s) => s.id === id);
        const j = i + direction;
        if (i < 0 || j < 0 || j >= ordered.length) return;
        [ordered[i], ordered[j]] = [ordered[j], ordered[i]];
        const reorder = ordered.map((s, idx) => ({ id: s.id, order: idx + 1 }));
        try {
            await adminApi.post("/cms/hero_slides/reorder", { items: reorder });
            toast.success("Order updated");
            load();
        } catch (e) { toast.error(formatErr(e)); }
    }

    return (
        <div data-testid="hero-slides-panel">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <div className="lnl-eyebrow">Homepage · Hero</div>
                    <h2 className="font-heading text-xl font-bold text-lnl-charcoal">Hero slides</h2>
                    <div className="text-xs text-lnl-gray">Each slide has its own photo, headline, CTA and motion style. Reorder with the up/down buttons.</div>
                </div>
                <button onClick={addSlide} disabled={busy} className="lnl-btn-primary" data-testid="hero-add-slide">+ New slide</button>
            </div>

            <div className="space-y-4">
                {items.sort((a, b) => a.order - b.order).map((s, idx) => (
                    <div key={s.id} className="lnl-card p-4" data-testid={`hero-slide-row-${s.id}`}>
                        <div className="flex items-start gap-4">
                            <div className="w-40 h-24 rounded-md border border-lnl-border bg-lnl-off-white overflow-hidden shrink-0">
                                {s.image_url
                                    ? <img src={s.image_url} alt={s.alt || ""} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                    : <div className="w-full h-full grid place-items-center text-xs text-lnl-gray">No image</div>}
                            </div>
                            <div className="flex-1 min-w-0 space-y-2">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    <input value={s.eyebrow || ""} onChange={(e) => patch(s.id, { eyebrow: e.target.value })} placeholder="Eyebrow (e.g. Admissions Now Open)" className="rounded-md border border-lnl-border px-2 py-1.5 text-sm" data-testid={`slide-eyebrow-${s.id}`} />
                                    <input value={s.headline || ""} onChange={(e) => patch(s.id, { headline: e.target.value })} placeholder="Headline" className="rounded-md border border-lnl-border px-2 py-1.5 text-sm font-heading font-semibold" data-testid={`slide-headline-${s.id}`} />
                                    <input value={s.subheading || ""} onChange={(e) => patch(s.id, { subheading: e.target.value })} placeholder="Subheading" className="rounded-md border border-lnl-border px-2 py-1.5 text-sm" />
                                    <input value={s.description || ""} onChange={(e) => patch(s.id, { description: e.target.value })} placeholder="Description" className="rounded-md border border-lnl-border px-2 py-1.5 text-sm" />
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    <div className="flex gap-1.5">
                                        <input value={s.primary_cta_text || ""} onChange={(e) => patch(s.id, { primary_cta_text: e.target.value })} placeholder="Primary CTA text" className="flex-1 rounded-md border border-lnl-border px-2 py-1.5 text-sm" data-testid={`slide-cta-text-${s.id}`} />
                                        <input value={s.primary_cta_url || ""} onChange={(e) => patch(s.id, { primary_cta_url: e.target.value })} placeholder="/admissions/apply" className="flex-1 rounded-md border border-lnl-border px-2 py-1.5 text-sm font-mono" data-testid={`slide-cta-url-${s.id}`} />
                                    </div>
                                    <div className="flex gap-1.5">
                                        <input value={s.secondary_cta_text || ""} onChange={(e) => patch(s.id, { secondary_cta_text: e.target.value })} placeholder="Secondary CTA text" className="flex-1 rounded-md border border-lnl-border px-2 py-1.5 text-sm" />
                                        <input value={s.secondary_cta_url || ""} onChange={(e) => patch(s.id, { secondary_cta_url: e.target.value })} placeholder="/academics" className="flex-1 rounded-md border border-lnl-border px-2 py-1.5 text-sm font-mono" />
                                    </div>
                                </div>
                                <div className="flex flex-wrap items-center gap-3 text-xs">
                                    <label className="inline-flex items-center gap-1"><input type="checkbox" checked={!!s.enabled} onChange={(e) => patch(s.id, { enabled: e.target.checked })} data-testid={`slide-enabled-${s.id}`} /> Enabled</label>
                                    <label className="inline-flex items-center gap-1"><input type="checkbox" checked={!!s.cta_new_tab} onChange={(e) => patch(s.id, { cta_new_tab: e.target.checked })} /> Open in new tab</label>
                                    <label className="inline-flex items-center gap-1">Motion:
                                        <select value={s.motion_style || "ken_burns"} onChange={(e) => patch(s.id, { motion_style: e.target.value })} className="rounded-md border border-lnl-border px-1.5 py-1 bg-white">
                                            <option value="ken_burns">Ken Burns</option>
                                            <option value="pan_left">Pan left</option>
                                            <option value="pan_right">Pan right</option>
                                            <option value="static">Static</option>
                                        </select>
                                    </label>
                                    <label className="inline-flex items-center gap-1">Duration:
                                        <input type="number" min="3" max="20" value={s.duration_seconds || 6} onChange={(e) => patch(s.id, { duration_seconds: Number(e.target.value) })} className="w-14 rounded-md border border-lnl-border px-1.5 py-1" /> s
                                    </label>
                                    <label className="inline-flex items-center gap-1">Overlay:
                                        <input type="range" min="0.15" max="0.85" step="0.05" value={s.overlay_opacity || 0.55} onChange={(e) => patch(s.id, { overlay_opacity: Number(e.target.value) })} className="w-24" />
                                        <span className="font-mono">{Math.round((s.overlay_opacity || 0.55) * 100)}%</span>
                                    </label>
                                </div>
                                <div className="flex flex-wrap gap-2 pt-1">
                                    <button onClick={() => setPickerFor(s.id)} className="lnl-btn-secondary text-xs py-1.5" data-testid={`slide-pick-img-${s.id}`}><ImageIcon className="h-3.5 w-3.5" /> Choose image</button>
                                    <button onClick={() => move(s.id, -1)} disabled={idx === 0} className="lnl-btn-secondary text-xs py-1.5" data-testid={`slide-up-${s.id}`}>↑ Up</button>
                                    <button onClick={() => move(s.id, +1)} disabled={idx === items.length - 1} className="lnl-btn-secondary text-xs py-1.5" data-testid={`slide-down-${s.id}`}>↓ Down</button>
                                    <button onClick={() => del(s.id)} className="text-xs text-rose-700 hover:underline font-heading font-semibold ml-auto self-center" data-testid={`slide-delete-${s.id}`}><Trash2 className="inline h-3 w-3" /> Delete</button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
                {items.length === 0 && <div className="p-10 text-center text-lnl-gray border border-dashed border-lnl-border rounded-md">No slides yet. Click <b>New slide</b> above.</div>}
            </div>

            <MediaPicker
                open={!!pickerFor}
                onClose={() => setPickerFor(null)}
                onPick={(m) => {
                    const url = `${API}/cms/media/${m.id}/download`;
                    patch(pickerFor, { image_url: url, alt: m.alt_text || m.title || "" });
                }}
            />
        </div>
    );
}

// ============================================================================
// Activity log — filterable
// ============================================================================
export function ActivityPanel() {
    const [rows, setRows] = useState([]);
    const [q, setQ] = useState("");
    const [action, setAction] = useState("");
    const [since, setSince] = useState("");
    const [until, setUntil] = useState("");
    const [busy, setBusy] = useState(false);

    async function load() {
        setBusy(true);
        try {
            const params = {};
            if (q) params.q = q;
            if (action) params.action = action;
            if (since) params.since = new Date(since).toISOString();
            if (until) params.until = new Date(until + "T23:59:59").toISOString();
            const { data } = await adminApi.get("/auth/activity", { params });
            setRows(data);
        } catch { /* fall back to cms activity for legacy */ }
        finally { setBusy(false); }
    }
    useEffect(() => { load(); }, []); // initial

    return (
        <div data-testid="activity-panel">
            <div className="lnl-card p-4 mb-4 grid grid-cols-1 sm:grid-cols-4 gap-3">
                <div>
                    <label className="text-xs text-lnl-gray font-heading font-semibold">Search actor / entity</label>
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="email or entity id" className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2 text-sm" data-testid="activity-q" />
                </div>
                <div>
                    <label className="text-xs text-lnl-gray font-heading font-semibold">Action prefix</label>
                    <input value={action} onChange={(e) => setAction(e.target.value)} placeholder="e.g. news, inquiry" className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2 text-sm" data-testid="activity-action" />
                </div>
                <div>
                    <label className="text-xs text-lnl-gray font-heading font-semibold">From</label>
                    <input type="date" value={since} onChange={(e) => setSince(e.target.value)} className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2 text-sm" data-testid="activity-since" />
                </div>
                <div>
                    <label className="text-xs text-lnl-gray font-heading font-semibold">To</label>
                    <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2 text-sm" data-testid="activity-until" />
                </div>
                <div className="sm:col-span-4 flex gap-2">
                    <button onClick={load} className="lnl-btn-primary text-sm py-2" data-testid="activity-apply" disabled={busy}>{busy ? "Loading…" : "Apply filters"}</button>
                    <button onClick={() => { setQ(""); setAction(""); setSince(""); setUntil(""); setTimeout(load, 0); }} className="lnl-btn-secondary text-sm py-2" data-testid="activity-reset">Reset</button>
                    <div className="ml-auto text-xs text-lnl-gray self-center">{rows.length} record{rows.length === 1 ? "" : "s"}</div>
                </div>
            </div>
            <div className="lnl-card overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                    <thead className="bg-lnl-off-white text-left text-xs uppercase tracking-widest text-lnl-gray"><tr><th className="p-2">When</th><th className="p-2">Actor</th><th className="p-2">Action</th><th className="p-2">Entity</th><th className="p-2">Details</th></tr></thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} className="border-t border-lnl-border">
                                <td className="p-2 text-xs text-lnl-gray">{new Date(r.at).toLocaleString()}</td>
                                <td className="p-2">{r.actor_email}</td>
                                <td className="p-2 font-heading font-semibold">{r.action}</td>
                                <td className="p-2 text-lnl-gray">{r.entity}/{r.entity_id}</td>
                                <td className="p-2 text-xs text-lnl-gray max-w-xs truncate">{r.meta && Object.keys(r.meta).length ? JSON.stringify(r.meta) : "—"}</td>
                            </tr>
                        ))}
                        {rows.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-lnl-gray">No activity matches.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ============================================================================
// Login activity — auth-only audit trail
// ============================================================================
export function LoginActivityPanel() {
    const [rows, setRows] = useState([]);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("");
    const [since, setSince] = useState("");
    const [until, setUntil] = useState("");
    const [busy, setBusy] = useState(false);

    async function load() {
        setBusy(true);
        try {
            const params = {};
            if (q) params.q = q;
            if (status) params.status = status;
            if (since) params.since = new Date(since).toISOString();
            if (until) params.until = new Date(until + "T23:59:59").toISOString();
            const { data } = await adminApi.get("/auth/activity/login", { params });
            setRows(data);
        } catch { /* ignore */ }
        finally { setBusy(false); }
    }
    useEffect(() => { load(); }, []);

    const badge = (a) => {
        if (a === "auth.login.success") return <span className="text-[10px] px-2 py-0.5 rounded border bg-emerald-50 text-emerald-800 border-emerald-200">Success</span>;
        if (a === "auth.login.failed")  return <span className="text-[10px] px-2 py-0.5 rounded border bg-rose-50 text-rose-800 border-rose-200">Failed</span>;
        if (a === "auth.login.blocked") return <span className="text-[10px] px-2 py-0.5 rounded border bg-amber-50 text-amber-800 border-amber-200">Blocked</span>;
        if (a === "auth.logout")        return <span className="text-[10px] px-2 py-0.5 rounded border bg-slate-50 text-slate-800 border-slate-200">Logout</span>;
        return <span className="text-[10px] text-lnl-gray">{a}</span>;
    };

    return (
        <div data-testid="login-activity-panel">
            <div className="lnl-card p-4 mb-4 grid grid-cols-1 sm:grid-cols-5 gap-3">
                <div>
                    <label className="text-xs text-lnl-gray font-heading font-semibold">Search user / IP</label>
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="email or ip" className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2 text-sm" data-testid="login-q" />
                </div>
                <div>
                    <label className="text-xs text-lnl-gray font-heading font-semibold">Status</label>
                    <select value={status} onChange={(e) => setStatus(e.target.value)} className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2 text-sm bg-white" data-testid="login-status">
                        <option value="">All</option>
                        <option value="success">Success</option>
                        <option value="failed">Failed</option>
                        <option value="blocked">Blocked</option>
                        <option value="logout">Logout</option>
                    </select>
                </div>
                <div>
                    <label className="text-xs text-lnl-gray font-heading font-semibold">From</label>
                    <input type="date" value={since} onChange={(e) => setSince(e.target.value)} className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2 text-sm" />
                </div>
                <div>
                    <label className="text-xs text-lnl-gray font-heading font-semibold">To</label>
                    <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2 text-sm" />
                </div>
                <div className="flex items-end gap-2">
                    <button onClick={load} className="lnl-btn-primary text-sm py-2 flex-1" disabled={busy} data-testid="login-apply">{busy ? "Loading…" : "Apply"}</button>
                    <button onClick={() => { setQ(""); setStatus(""); setSince(""); setUntil(""); setTimeout(load, 0); }} className="lnl-btn-secondary text-sm py-2">Reset</button>
                </div>
            </div>
            <div className="lnl-card overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                    <thead className="bg-lnl-off-white text-left text-xs uppercase tracking-widest text-lnl-gray"><tr><th className="p-2">When</th><th className="p-2">Status</th><th className="p-2">User</th><th className="p-2">IP</th><th className="p-2">User agent</th></tr></thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} className="border-t border-lnl-border">
                                <td className="p-2 text-xs text-lnl-gray whitespace-nowrap">{new Date(r.at).toLocaleString()}</td>
                                <td className="p-2">{badge(r.action)}</td>
                                <td className="p-2 font-heading font-semibold">{r.actor_email}</td>
                                <td className="p-2 text-xs text-lnl-gray">{r.meta?.ip || "—"}</td>
                                <td className="p-2 text-[11px] text-lnl-gray max-w-[280px] truncate" title={r.meta?.ua || ""}>{r.meta?.ua || "—"}</td>
                            </tr>
                        ))}
                        {rows.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-lnl-gray">No sign-in activity.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
